import Highcharts from 'highcharts';

import {
  findRollupByGroupingValue,
  groupingValueFor,
  totalDimsFor,
} from 'components/insights/careTransitions/shared/slideout/rollupHelpers';
import { Metrics } from 'components/insights/constants';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { colors } from 'styles/theme/colors';
import textStyles from 'styles/theme/textStyles';

import { NetworkPerformanceEntityRow } from '../useNetworkPerformanceSlideoutAnalytics';

const POINT_WIDTH = 40;

/** Adds a dotted reference line (plotLine + legend-only line series) to chart options. */
export function withReferenceLine(
  options: Highcharts.Options,
  { value, label, yAxisMax }: { value: number; label: string; yAxisMax?: number }
): Highcharts.Options {
  return {
    ...options,
    legend: {
      enabled: true,
      align: 'center' as const,
      verticalAlign: 'bottom' as const,
      symbolRadius: 6,
      itemStyle: { fontSize: '12px', fontWeight: 'normal', color: colors.black75 },
    },
    yAxis: {
      ...(options.yAxis as Highcharts.YAxisOptions),
      ...(yAxisMax != null ? { min: 0, max: yAxisMax } : {}),
      plotLines: [{ color: colors.black75, width: 2, value, dashStyle: 'ShortDash' as const }],
    },
    series: [
      ...(options.series as Highcharts.SeriesOptionsType[]),
      {
        type: 'line' as const,
        name: label,
        color: colors.black75,
        dashStyle: 'ShortDash' as const,
        data: [value],
        marker: { enabled: false },
        enableMouseTracking: false,
      },
    ],
  };
}

/** Extracts per-entity values for a metric from rollups as a name→value map.
 *  Same extraction logic as extractEntityRows in the slideout hook, but returns a Map. */
export function extractEntityMetricMap(
  rollups: AnalyticsResponseRollupCollection,
  metric: Metrics
): Map<string, number> {
  const map = new Map<string, number>();
  const totalDims = totalDimsFor(rollups);
  const entityTarget = groupingValueFor(totalDims, new Set([0]));

  if (entityTarget === 0) {
    const { analyticsResponse } = rollups;
    const metricIdx = analyticsResponse.findMetricIndex(metric);
    if (metricIdx < 0) return map;
    for (const row of analyticsResponse.responseData) {
      if ((row.grouping ?? 0) !== 0 || (row.datesIndex ?? 0) !== 0) continue;
      const name = String(row.dimensionValues[0]?.value ?? '');
      if (name) map.set(name, Number(row.metricValues[metricIdx]?.value ?? 0));
    }
    return map;
  }

  const entityRollup = findRollupByGroupingValue(rollups, entityTarget);
  if (!entityRollup) return map;
  const names = entityRollup.dimensions.getCurrentDimensionValuesByIndex(0);
  const values = entityRollup.metrics.getCurrentSeries(metric);
  names.forEach((name, i) => map.set(name, values[i] ?? 0));
  return map;
}

/** Adds a sublabel (e.g. "Patients: 42") below each x-axis entity name. */
export function withXAxisSublabel(
  options: Highcharts.Options,
  countByName: Map<string, number>,
  sublabel: string
): Highcharts.Options {
  const xAxis = options.xAxis as Highcharts.XAxisOptions;
  return {
    ...options,
    xAxis: {
      ...xAxis,
      labels: {
        ...xAxis.labels,
        useHTML: false,
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
          const name = String(this.value);
          const count = countByName.get(name) ?? 0;
          return `<span style="color:${colors.black75};font-size:11px;">${name}</span><br/><span style="color:${colors.black50};font-size:8px;">${sublabel}: ${count}</span>`;
        },
      },
    },
  };
}

const calculateWidth = (pointWidth: number, dataLength: number) => {
  return (pointWidth + pointWidth * 1.5) * dataLength;
};

export type ScrollConfig = {
  minWidth: number;
  legend?: { label: string; color: string };
};

type ColumnChartParams = {
  entityRows: NetworkPerformanceEntityRow[];
  seriesName: string;
  color: string;
  yAxisFormat?: string;
  yAxisAllowDecimals?: boolean;
  dataLabelsFormatter?: Highcharts.DataLabelsFormatterCallbackFunction;
};

type ColumnChartResult = {
  options: Highcharts.Options;
  scrollConfig: ScrollConfig;
};

export function buildColumnChartOptions({
  entityRows,
  seriesName,
  color,
  yAxisFormat,
  yAxisAllowDecimals,
  dataLabelsFormatter,
}: ColumnChartParams): ColumnChartResult {
  const sorted = [...entityRows].sort((a, b) => b.value - a.value);

  return {
    options: {
      chart: {
        type: 'column',
        style: { fontFamily: textStyles.base.fontFamily },
        height: 280,
      },
      accessibility: { enabled: false },
      credits: { enabled: false },
      title: { text: undefined },
      legend: { enabled: false },
      xAxis: {
        categories: sorted.map((r) => r.name),
        lineColor: colors.black10,
        tickLength: 0,
        labels: { style: { fontSize: '11px', color: colors.black75 } },
      },
      yAxis: {
        title: { text: undefined },
        gridLineColor: colors.black10,
        allowDecimals: yAxisAllowDecimals,
        labels: {
          ...(yAxisFormat ? { format: yAxisFormat } : {}),
          style: { fontSize: '11px', color: colors.black50 },
        },
      },
      tooltip: { enabled: false },
      plotOptions: {
        column: {
          borderRadius: { radius: 3, scope: 'point' as const, where: 'end' as const },
          pointWidth: POINT_WIDTH,
          dataLabels: {
            enabled: true,
            ...(dataLabelsFormatter ? { formatter: dataLabelsFormatter } : {}),
            style: { fontSize: '10px', fontWeight: 'normal', color: colors.black75 },
          },
        },
        series: {
          animation: false,
          inactiveOtherPoints: true,
          events: { legendItemClick: () => false },
        },
      },
      series: [
        {
          type: 'column',
          name: seriesName,
          data: sorted.map((r) => r.value),
          color,
        },
      ],
    },
    scrollConfig: {
      minWidth: calculateWidth(POINT_WIDTH, sorted.length),
      legend: { label: seriesName, color },
    },
  };
}
